import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  useToast
} from '@chakra-ui/react';
import { eventService } from '../../services/eventService';
import { useAuth } from '../../hooks/useAuth';
import { PlacesAutocomplete } from '../Common/PlacesAutocomplete';
import { ImageUpload } from '../Common/ImageUpload';

export const CreateEventForm = ({ onEventCreated }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { user } = useAuth();
  const toast = useToast();

  // Reserved words that cannot be used as slugs
  const RESERVED_SLUGS = [
    'admin', 'auth', 'events', 'fighters', 'history', 'leaderboard',
    'profile', 'api', 'login', 'signup', 'dashboard', 'settings'
  ];

  // Generate mobile-friendly slug from event name (no dashes)
  const generateSlug = (eventName) => {
    return eventName
      .replace(/[^a-zA-Z0-9]+/g, '') // Remove all non-alphanumeric, keep original case
      .substring(0, 30); // Shorter limit for mobile typing
  };

  // Sanitize slug input to only allow URL-friendly characters
  const sanitizeSlug = (input) => {
    return input.replace(/[^a-zA-Z0-9]/g, ''); // Only allow alphanumeric characters
  };

  // Generate lowercase version for URL routing
  const generateSlugKey = (slug) => {
    return slug.toLowerCase();
  };

  // Validate slug for uniqueness and reserved words
  const validateSlug = async (slugToValidate) => {
    if (!slugToValidate) return { isValid: false, error: 'Slug is required' };

    const slugKey = generateSlugKey(slugToValidate);

    if (RESERVED_SLUGS.includes(slugKey)) {
      return { isValid: false, error: 'This slug is reserved and cannot be used' };
    }

    try {
      const isAvailable = await eventService.isSlugAvailable(slugKey);
      if (!isAvailable) {
        return { isValid: false, error: 'This slug is already taken' };
      }
      return { isValid: true };
    } catch (err) {
      return { isValid: false, error: 'Error checking slug availability' };
    }
  };

  // Auto-generate slug when name changes
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(newName));
    }
  };

  // Handle slug input with real-time sanitization
  const handleSlugChange = (e) => {
    const sanitizedSlug = sanitizeSlug(e.target.value);
    setSlug(sanitizedSlug);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const eventDate = new Date(`${date}T${time}`);
      const finalSlug = slug || generateSlug(name);

      // Validate slug before creating event
      const slugValidation = await validateSlug(finalSlug);
      if (!slugValidation.isValid) {
        setError(slugValidation.error);
        setLoading(false);
        return;
      }

      // Handle location data - could be string or object with coordinates
      let locationData = location;
      let locationDetails = null;

      if (typeof location === 'object' && location !== null) {
        // eslint-disable-next-line no-prototype-builtins
        locationData = location.hasOwnProperty('address') ? location.address : JSON.stringify(location);
        locationDetails = location;
      }

      const eventId = await eventService.createEvent({
        name,
        slug: finalSlug, // Display version (RCF14)
        slugKey: generateSlugKey(finalSlug), // Routing version (rcf14)
        date: eventDate,
        location: locationData,
        locationDetails,
        description,
        fights: [],
        isActive: true,
        createdBy: user.uid
      });

      // Upload cover image if provided
      if (coverImage) {
        try {
          await eventService.updateEventCoverImage(eventId, coverImage);
        } catch (imageError) {
          console.error('Error uploading cover image:', imageError);
          // Don't fail the entire event creation for image upload issues
          toast({
            title: 'Event created, but cover image upload failed',
            description: 'You can add a cover image later by editing the event.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }

      toast({
        title: 'Event created successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setName('');
      setSlug('');
      setDate('');
      setTime('');
      setLocation('');
      setDescription('');
      setCoverImage(null);

      onEventCreated(eventId);
    } catch (err) {
      setError(err.message || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="lg" mx="auto" p={6} borderWidth={1} borderRadius="lg" shadow="lg">

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <VStack spacing={4}>
          <FormControl isRequired>
            <FormLabel>Event Name</FormLabel>
            <Input
              value={name}
              onChange={handleNameChange}
              placeholder="e.g., RCF Championship Night"
            />
          </FormControl>

          <FormControl>
            <FormLabel>URL Slug</FormLabel>
            <Input
              value={slug}
              onChange={handleSlugChange}
              placeholder="e.g., RCF14 or UFC300MainEvent"
            />
            <Text fontSize="sm" color="gray.400" mt={1}>
              This creates a short URL: fighterbetplus.com/{slug || 'your-slug'}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Only letters and numbers allowed - spaces and special characters will be removed
            </Text>
          </FormControl>

          <HStack width="full" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Date</FormLabel>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Time</FormLabel>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </FormControl>
          </HStack>

          <FormControl isRequired>
            <FormLabel>Location</FormLabel>
            <PlacesAutocomplete
              value={location}
              onChange={setLocation}
              placeholder="e.g., Muskogee Civic Center, Muskogee, OK"
              isRequired
            />
          </FormControl>

          <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Event description (optional)"
              rows={3}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Cover Image</FormLabel>
            <ImageUpload
              value={coverImage}
              onChange={setCoverImage}
              placeholder="Upload a cover image for the event"
              aspectRatio="16/9"
              maxWidth="100%"
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              Optional - This image will be displayed on the event page and in event listings
            </Text>
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            width="full"
            isLoading={loading}
            loadingText="Creating event..."
          >
            Create Event
          </Button>
        </VStack>
      </form>
    </Box>
  );
};
